import * as React from 'react';

import * as api from '@gitbook/api-client';
import { Link } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { Alert, type AlertKind, type IconDefinition, LoadingIndicator } from '@gitbook/spine-react';

import { useNavigation } from '../../navigation';
import { useGitSyncCurrentStatus } from './useGitSyncCurrentStatus';

/**
 * Show status of the current GitSync installation.
 */
export function GitSyncInstallationStatus({
    space,
    isLegacyInstallation,
}: {
    space: api.Space;
    isLegacyInstallation: boolean;
}): React.ReactElement | null {
    const { getNavigationLinkProps } = useNavigation();
    const gitSyncState = useGitSyncCurrentStatus(space);

    const [displayProgress, setDisplayProgress] = React.useState(gitSyncState?.progress ?? 0);
    React.useEffect(() => {
        if (gitSyncState?.progress && displayProgress < gitSyncState?.progress) {
            setDisplayProgress(gitSyncState.progress);
        }
    }, [gitSyncState?.progress, displayProgress]);

    const detailsModalLink = getNavigationLinkProps({
        screen: {
            type: 'space',
            space: space.id,
            sideSheet: {
                type: 'gitsync',
                tab: 'progress',
            },
        },
    });

    if (isLegacyInstallation) {
        return (
            <GitSyncStatus kind="danger" icon={Icon.X} title="Invalid token">
                Your GitHub token has expired. Upgrade to the newer GitHub integration.
            </GitSyncStatus>
        );
    }

    if (!gitSyncState) {
        return null;
    }

    if (gitSyncState.state === 'timed-out') {
        return (
            <GitSyncStatus kind="danger" icon={Icon.X} title="Sync failed">
                The latest sync timed out. Try again or contact support for help.{' '}
                <Link kind="in-text-muted" {...detailsModalLink}>
                    See details
                </Link>
            </GitSyncStatus>
        );
    }

    if (gitSyncState.state === 'running') {
        if (gitSyncState.direction === api.GitSyncOperationDirection.Import) {
            return (
                <GitSyncStatus
                    icon={LoadingIndicator}
                    title={`Sync in progress ${displayProgress.toFixed(0)}%`}
                >
                    Importing repository content. This may take a few minutes depending on the size
                    of your content.{' '}
                    <Link kind="in-text-muted" {...detailsModalLink}>
                        See details
                    </Link>
                </GitSyncStatus>
            );
        }

        return (
            <GitSyncStatus
                icon={LoadingIndicator}
                title={`Sync in progress ${displayProgress.toFixed(0)}%`}
            >
                Exporting space content to your repository. This may take a few minutes depending on
                the size of your content.{' '}
                <Link kind="in-text-muted" {...detailsModalLink}>
                    See details
                </Link>
            </GitSyncStatus>
        );
    }

    if (gitSyncState.state === 'failure') {
        return (
            <GitSyncStatus kind="danger" icon={Icon.X} title="Sync failed">
                Click{' '}
                <Link kind="in-text-muted" {...detailsModalLink}>
                    here
                </Link>{' '}
                to see more details and possible solutions, or contact support for help.
            </GitSyncStatus>
        );
    }

    return (
        <GitSyncStatus kind="success" icon={Icon.Check} title="Sync complete">
            Your space is synced with Git! You can continue editing your content and we'll keep
            everything in sync.
        </GitSyncStatus>
    );
}

/** Render a visual status */
export function GitSyncStatus(props: {
    kind?: AlertKind;
    icon: IconDefinition;
    title: string;
    children: React.ReactNode;
}): React.ReactElement {
    const { kind = 'info', icon, title, children } = props;

    return <Alert kind={kind} size="small" icon={icon} title={title} details={children} />;
}
